/*
 *  AbstractFirebaseController.js
 *
 * NOTE: Its important to use javascript that works in the frontend and the
 * backend.
 *
 */
//==============================================================================
// External dependencies
//==============================================================================
const _ = require("lodash");

const {
  AbstractFirebaseController__AtsStats,
  AbstractFirebaseController__AccountSystem,
  AbstractFirebaseController__MemberSystem,
  AbstractFirebaseController__Organizations
} = require("@talentbait/shared-components/public/build/backend-output");

const { LINKEDIN_PURCHASE_PRICE } = require("../common/constants/pricing");

//==============================================================================
// Helper
//==============================================================================
function guid() {
  function s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }
  return s4() + s4() + "-" + s4() + "-" + s4() + "-" + s4() + "-" + s4() + s4() + s4();
}

function enrichSnapshotWithId(querySnapshot) {
  const data = [];
  querySnapshot.forEach((doc) => {
    const elem = doc.data();
    elem.id = doc.id;
    data.push(elem);
  });
  return data;
}
//==============================================================================
// Module
//==============================================================================
module.exports = function (firebase, logger) {
  const MixinCampaigns = require("./AbstractFirebaseController__Campaigns.js")(firebase, logger);
  const MixinAdsets = require("./AbstractFirebaseController__Adsets.js")(firebase, logger);
  const MixinAdPreview = require("./AbstractFirebaseController__AdPreview.js")(firebase, logger);
  const MixinChannelCampaigns = require("./AbstractFirebaseController__ChannelCampaigns.js")(
    logger
  );

  const MixinCampaignMembers = require("./AbstractFirebaseController__CampaignMembers.js")(
    firebase,
    logger
  );
  const MixinCoupons = require("./AbstractFirebaseController__Coupons.js")(firebase, logger);
  const MixinBookingSessions = require("./AbstractFirebaseController__BookingSessions.js")(logger);
  const MixinMediaAssets = require("./AbstractFirebaseController__MediaAssets.js")(
    firebase,
    logger
  );
  const MixinPrepaidTransactions = require("./AbstractFirebaseController__PrepaidTransactions.js")(
    logger
  );
  const MixinApplicantSystem = require("./AbstractFirebaseController__ApplicantSystem")(
    firebase,
    logger
  );

  const MixinReports = require("./AbstractFirebaseController__Reports")(logger);
  const MixinKeywords = require("./AbstractFirebaseController__Keywords")(logger);
  const MixinAssets = require("./AbstractFirebaseController__Assets")(firebase, logger);
  const MixinUsers = require("./AbstractFirebaseController__Users")(firebase, logger);
  const MixinCandidates = require("./AbstractFirebaseController__Candidates")(firebase, logger);
  const MixinUserLogsFlat = require("./AbstractFirebaseController__UserLogsFlat")(firebase, logger);
  const MixinOrganization = require("./AbstractFirebaseController__Organization")(firebase, logger);
  const MixinRoles = require("./AbstractFirebaseController__Roles")(firebase, logger);
  const MixinDailyMetricsMixin = require("./AbstractFirebaseController__DailyMetrics")(
    firebase,
    logger
  );
  const MixinBrandKitsMixin = require("./AbstractFirebaseController__BrandKits")(firebase, logger);
  const MixinPrepaidStateMixin = require("./AbstractFirebaseController__PrepaidState")(
    firebase,
    logger
  );
  const MixinAts = require("./AbstractFirebaseController__Ats")(firebase, logger);

  const MixinCandidateLogs = require("./AbstractFirebaseController__CandidateLogs")(
    firebase,
    logger
  );

  // Shared Firebase Methods
  const MixinAtsStats = AbstractFirebaseController__AtsStats(firebase, logger);
  const MixinAccountSystem = AbstractFirebaseController__AccountSystem(firebase, logger);
  const MixinMemberSystem = AbstractFirebaseController__MemberSystem(firebase, logger);
  const MixinOrganizations = AbstractFirebaseController__Organizations(firebase, logger);

  class FirebaseController {
    //==============================================================================
    // Users Collection
    //==============================================================================
    collection(collectionName) {
      return firebase.firestore().collection(collectionName);
    }
    collectionGroup(collectionGroupName) {
      return firebase.firestore().collectionGroup(collectionGroupName);
    }
    batch() {
      return firebase.firestore().batch();
    }
    increment(value = 1) {
      return firebase.firestore.FieldValue.increment(value);
    }

    getUserWithId(userId) {
      return new Promise((fullfil, reject) => {
        this.collection("users")
          .doc(userId)
          .get()
          .then((querySnapshot) =>
            fullfil({
              id: querySnapshot.id,
              ...querySnapshot.data()
            })
          )
          .catch(reject);
      });
    }

    getUserByMessageNotification(timeInterval) {
      return new Promise((fullfil, reject) => {
        this.collection("users")
          .where("notifcationFrequencyNewMessages", "==", timeInterval)
          .get()
          .then((querySnapshot) =>
            fullfil({
              id: querySnapshot.id,
              ...querySnapshot.data()
            })
          )
          .catch(reject);
      });
    }

    //==============================================================================
    // Stock pictures Collection
    //==============================================================================
    getStockPicturesByIds(imageIds) {
      return new Promise((fullfil, reject) => {
        this.collection("stock_pictures")
          .where("imageId", "in", imageIds)
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }

    saveStockPicture(picture) {
      return new Promise((fullfil, reject) => {
        this.collection("stock_pictures").add(picture).then(fullfil).catch(reject);
      });
    }

    loadAllPackages() {
      return new Promise((fullfil, reject) => {
        this.collection("social_packages")
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }

    loadPackagesByOrga(orgaId) {
      return new Promise((fullfil, reject) => {
        this.collection("social_packages")
          .where("orgaId", "==", orgaId)
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }
    createOrganizations(orgaData) {
      return new Promise((fullfil, reject) => {
        this.collection("organizations").add(orgaData).then(fullfil).catch(reject);
      });
    }

    saveBudgetCheck(orgaData) {
      return new Promise((fullfil, reject) => {
        this.collection("budget_checks").add(orgaData).then(fullfil).catch(reject);
      });
    }

    incrementOrgaField(orgaId, fieldName, incrementValue) {
      return new Promise((fullfil, reject) => {
        this.collection("organizations")
          .doc(orgaId)
          .update({
            [fieldName]: this.increment(incrementValue)
          })
          .then(fullfil)
          .catch(reject);
      });
    }

    updateOrganization(organizationId, updateData) {
      return new Promise((fullfil, reject) => {
        this.collection("organizations")
          .doc(organizationId)
          .set(updateData, { merge: true })
          .then(fullfil)
          .catch(reject);
      });
    }

    getAllOrganizations() {
      return new Promise((fullfil, reject) => {
        this.collection("organizations")
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }

    getOrganizationById(organizationId) {
      return new Promise((fullfil, reject) => {
        this.collection("organizations").doc(organizationId).get().then(fullfil).catch(reject);
      });
    }

    findOrganizationWithId(organizationId) {
      return new Promise((fullfil, reject) => {
        this.collection("organizations")
          .doc(organizationId)
          .get()
          .then((querySnapshot) =>
            fullfil({
              id: querySnapshot.id,
              ...querySnapshot.data()
            })
          )
          .catch(reject);
      });
    }
    fetchAllErrorMessages() {
      return new Promise((fullfil, reject) => {
        this.collection("messages")
          .where("error", "!=", null)
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }
    addPackagesToOrga(newPackage) {
      return new Promise((fullfil, reject) => {
        this.collection("social_packages").add(newPackage).then(fullfil).catch(reject);
      });
    }
    removePackage(packageId) {
      return new Promise((fullfil, reject) => {
        this.collection("social_packages").doc(packageId).delete().then(fullfil).catch(reject);
      });
    }
    editPackage(packageId, data) {
      return new Promise((fullfil, reject) => {
        this.collection("social_packages").doc(packageId).update(data).then(fullfil).catch(reject);
      });
    }
    getPackageByPackageId(packageId) {
      return new Promise((fullfil, reject) => {
        this.collection("social_packages")
          .where("packageId", "==", packageId)
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }

    updateMetricsByChannel(campaignId, channel) {
      const increment = firebase.firestore.FieldValue.increment(1);
      const storyRef = firebase.firestore().collection("campaigns").doc(campaignId);

      return storyRef.update({
        [`metrics.${channel}.totalClicks`]: increment,
        [`metrics.${channel}.totalSpent`]: 1,
        [`metrics.${channel}.platform`]: channel
      });
    }

    getTargetingLayer(startAfter, limit, nameFilter) {
      logger.log("database:getTargetingLayer", "called", { startAfter, limit, nameFilter });

      return new Promise((fullfil, reject) => {
        let query = this.collection("job_categories").orderBy("campaignsCount", "desc");
        if (startAfter) {
          query = query.startAfter(startAfter);
        }
        if (limit) {
          query = query.limit(limit);
        }
        if (nameFilter) {
          query = this.collection("job_categories").orderBy("campaignsCount", "desc");
        }
        query
          .get()
          .then((querySnapshot) => {
            logger.debug("database:getTargetingLayer", "finished", { len: querySnapshot.size });
            let payload = querySnapshot.docs.map((doc) => {
              return _.extend({}, doc.data(), { id: doc.id });
            });
            let newStartAfter = undefined;
            if (startAfter || limit) {
              newStartAfter = querySnapshot.docs[querySnapshot.size - 1];
            }
            if (nameFilter) {
              payload = payload.filter((doc) => {
                return doc.name.toLowerCase().includes(nameFilter.toLowerCase());
              });
            }
            return fullfil({ payload, startAfter: newStartAfter });
          })
          .catch(reject);
      });
    }
    deleteTargetingLayerByChannel(id, channelField, data) {
      return new Promise((fullfil, reject) => {
        this.collection("job_categories")
          .doc(id)
          .update({ [channelField]: firebase.firestore.FieldValue.arrayRemove(data) })
          .then(fullfil)
          .catch(reject);
      });
    }
    addTargetingLayerByChannel(id, channelField, data) {
      return new Promise((fullfil, reject) => {
        this.collection("job_categories")
          .doc(id)
          .update({ [channelField]: firebase.firestore.FieldValue.arrayUnion(data) })
          .then(fullfil)
          .catch(reject);
      });
    }
    updateTotalViewsApplicationForm(campaignId) {
      const increment = firebase.firestore.FieldValue.increment(1);
      const storyRef = firebase.firestore().collection("campaigns").doc(campaignId);

      return storyRef.update({
        totalViewsApplicationForm: increment
      });
    }

    async updateLinkedStats(campaignId) {
      const increment = firebase.firestore.FieldValue.increment(1);
      const storyRef = firebase.firestore().collection("campaigns").doc(campaignId);

      const snapshot = await this.collection("channel_campaigns")
        .where("campaignId", "==", campaignId)
        .where("channel", "==", "linkedin")
        .get();

      snapshot.forEach((doc) => {
        const channelSetId = doc.id;

        const channelSetRef = firebase
          .firestore()
          .collection("channel_campaigns")
          .doc(channelSetId);

        channelSetRef.update({
          "metrics.totalClicks": increment,
          "metrics.totalImpressions": increment,
          "metrics.totalSpent": LINKEDIN_PURCHASE_PRICE,
          "metrics.platform": "linkedin",
          "metrics.lastUpdate": new Date().toISOString(),
          "metricsDaily.totalClicks": increment,
          "metricsDaily.totalImpressions": increment,
          "metricsDaily.totalSpent": LINKEDIN_PURCHASE_PRICE,
          "metricsDaily.platform": "linkedin",
          "metricsDaily.lastUpdate": new Date().toISOString()
        });
      });

      return storyRef.update({
        "metrics.linkedin.totalClicks": increment,
        "metrics.linkedin.totalImpressions": increment,
        "metrics.linkedin.totalSpent": LINKEDIN_PURCHASE_PRICE,
        "metrics.linkedin.platform": "linkedin",
        "metrics.linkedin.lastUpdate": new Date().toISOString(),
        "metricsDaily.linkedin.totalClicks": increment,
        "metricsDaily.linkedin.totalImpressions": increment,
        "metricsDaily.linkedin.totalSpent": LINKEDIN_PURCHASE_PRICE,
        "metricsDaily.linkedin.platform": "linkedin",
        "metricsDaily.linkedin.lastUpdate": new Date().toISOString()
      });
    }

    //==============================================================================
    // Notifications Collection
    //==============================================================================

    getAllNotifications() {
      return new Promise((fullfil, reject) => {
        this.collection("notifications")
          .where("type", "==", "client_comment")
          .get()
          .then(fullfil)
          .catch(reject);
      });
    }

    fetchAllUsers() {
      return new Promise((fullfil, reject) => {
        this.collection("users")
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }

    updateUser(userId, newData) {
      return new Promise((fulfil, reject) => {
        this.collection("users").doc(userId).update(newData).then(fulfil).catch(reject);
      });
    }

    incrementUserField(userId, fieldName, incrementValue) {
      return new Promise((fullfil, reject) => {
        this.collection("users")
          .doc(userId)
          .update({
            [fieldName]: this.increment(incrementValue)
          })
          .then(fullfil)
          .catch(reject);
      });
    }

    getNotificationWithId(notificationId) {
      return this.getSingleEntry("notifications", notificationId);
    }
    getActiveFeeds() {
      return new Promise((fullfil, reject) => {
        this.collection("feeds").where("status", "==", "ACTIVE").get().then(fullfil).catch(reject);
      });
    }
    //==============================================================================
    // Comments
    //==============================================================================
    getAllComments(userId) {
      return new Promise((fullfil, reject) => {
        let query = this.collection("comments");
        if (userId) {
          query = query.where("userId", "==", userId);
        }
        //query = query.where("meta.initiator", "==", "posted");
        query = query.where("meta.type", "==", "comment");

        query
          .limit(500)
          .orderBy("createDate", "desc")
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }

    getNotes(orgaId, campaignId) {
      return new Promise((fullfil, reject) => {
        this.collection("campaign_notes")
          .where("campaignId", "==", campaignId)
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }
    storeNote(data) {
      return new Promise((fullfil, reject) => {
        this.collection("campaign_notes").add(data).then(fullfil).catch(reject);
      });
    }
    //==============================================================================
    // Job Categories
    //==============================================================================
    updateJobCategory(jobCategoryId, updates) {
      return new Promise((fullfil, reject) => {
        this.collection("job_categories")
          .doc(jobCategoryId)
          .set(updates, { merge: true })
          .then(fullfil)
          .catch(reject);
      });
    }
    loadJobCategories() {
      return new Promise((fullfil, reject) => {
        this.collection("job_categories")
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }

    saveGoogleAudienceIdToJobCategoryAudience(jobCategoryId, audienceId, googleAudienceId) {
      return new Promise((fullfil, reject) => {
        this.collection("job_categories")
          .doc(jobCategoryId)
          .collection("audiences")
          .doc(audienceId)
          .set({ audienceId: googleAudienceId }, { merge: true })
          .then(fullfil)
          .catch(reject);
      });
    }

    loadJobCategoriesAndAudiences() {
      return new Promise((fullfil, reject) => {
        this.collection("job_categories")
          .get()
          .then((querySnapshot) => {
            const enrichedDataPromises = querySnapshot.docs.map((doc) => {
              const data = doc.data();
              return doc.ref
                .collection("audiences")
                .get()
                .then((audiencesSnapshot) => {
                  const audiences = audiencesSnapshot.docs.map((audDoc) => ({
                    id: audDoc.id,
                    ...audDoc.data()
                  }));
                  return {
                    id: doc.id,
                    ...data,
                    audiences
                  };
                });
            });

            // Wait for all promises to resolve
            return Promise.all(enrichedDataPromises);
          })
          .then((enrichedData) => fullfil(enrichedData))
          .catch(reject);
      });
    }

    loadJobCategoriesWithRankings() {
      return new Promise((fullfil, reject) => {
        this.collection("job_categories")
          .where("level", "==", "1")
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }
    getAudiencesByChannel(jobCategoryId, channel, accountId) {
      logger.debug("AbstractFirebaseController:getAudiencesByChannel", "Was called with", {
        jobCategoryId,
        channel,
        accountId
      });
      return new Promise((fullfil, reject) => {
        this.collection("job_categories")
          .doc(jobCategoryId)
          .collection("audiences")
          .where("platformAccountId", "==", accountId)
          .where("channel", "==", channel)
          .get()
          .then((snapshot) => {
            const data = snapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id
            }));
            logger.debug(
              "AbstractFirebaseController:loadJobCategory",
              "Database call returned",
              data
            );
            fullfil(data);
          })
          .catch((err) => {
            logger.log("error:AbstractFirebaseController:loadJobCategory", "Database call failed", {
              err,
              jobCategoryId
            });
            reject(err);
          });
      });
    }
    loadJobCategory(jobCategoryId) {
      logger.debug("AbstractFirebaseController:loadJobCategory", "Was called with", {
        jobCategoryId
      });
      return new Promise((fullfil, reject) => {
        this.collection("job_categories")
          .doc(jobCategoryId)
          .get()
          .then((doc) => {
            const data = {
              ...doc.data(),
              id: doc.id
            };
            logger.debug(
              "AbstractFirebaseController:loadJobCategory",
              "Database call returned",
              data
            );
            fullfil(data);
          })
          .catch((err) => {
            logger.log("error:AbstractFirebaseController:loadJobCategory", "Database call failed", {
              err,
              jobCategoryId
            });
            reject(err);
          });
      });
    }
    //==============================================================================
    // Teaser text
    //==============================================================================
    saveTeaserText(data) {
      return new Promise((fullfil, reject) => {
        this.collection("teasertext_templates").add(data).then(fullfil).catch(reject);
      });
    }
    getTeaserTextsByJobCategoryId(jobCategoryId) {
      return new Promise((fullfil, reject) => {
        this.collection("teasertext_templates")
          .where("jobCategoryId", "==", jobCategoryId)
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }
    getAllTeaserTexts() {
      return new Promise((fullfil, reject) => {
        this.collection("teasertext_templates")
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }

    updateTeaser(id, data) {
      return new Promise((fullfil, reject) => {
        this.collection("teasertext_templates").doc(id).update(data).then(fullfil).catch(reject);
      });
    }

    getAllTeaserTextsByLanguage(language = "de") {
      return new Promise((fullfil, reject) => {
        this.collection("teasertext_templates")
          .where("language", "==", language)
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }

    fetchAllComments(orgaId, campaignId) {
      return new Promise((fullfil, reject) => {
        this.collection("comments")
          .where("campaignId", "==", campaignId)
          .where("orgaId", "==", orgaId)
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }
    deleteComment(orgaId, campaignId, commentId) {
      return new Promise((fullfil, reject) => {
        this.collection("comments").doc(commentId).delete().then(fullfil).catch(reject);
      });
    }
    createComment(data) {
      return new Promise((fullfil, reject) => {
        this.collection("comments").add(data).then(fullfil).catch(reject);
      });
    }

    //==============================================================================
    // Facebook Pages
    //==============================================================================
    saveFacebookPages(pages) {
      return new Promise((fullfil, reject) => {
        const pagesLength = pages.length;
        logger.debug("AbstractFirebaseController:saveFacebookPages", "called", pagesLength);
        const updatedDate = new Date().toISOString();

        // Divide the pages to 2 groups since we can only save a maximum
        // of 500 documents at a time on firebase using batches
        const firstGroupPages = pages.slice(0, 500);
        const secondGroupPages = pages.slice(500);

        // prepare the first set of pages
        let currentBatchWrite = firebase.firestore().batch();
        firstGroupPages.forEach((page) => {
          const pageId = page.id;

          // Add more details about the page
          page = _.extend(page, { platform: "facebook", updatedDate });
          const firebasePageId = "fb-" + pageId;

          currentBatchWrite.set(this.collection("social_accounts").doc(firebasePageId), page, {
            merge: true
          });
        });

        // Save the first page group
        currentBatchWrite
          .commit()
          .then(() => {
            logger.debug("AbstractFirebaseController:saveFacebookPages", "saved first batch");

            // prepare second group of pages
            currentBatchWrite = firebase.firestore().batch();
            secondGroupPages.forEach((page) => {
              const pageId = page.id;

              // Add more details about the page
              page = _.extend(page, { platform: "facebook", updatedDate });
              const firebasePageId = "fb-" + pageId;

              currentBatchWrite.set(this.collection("social_accounts").doc(firebasePageId), page, {
                merge: true
              });
            });

            // Save the second and last group of pages
            currentBatchWrite
              .commit()
              .then(() => {
                logger.debug("AbstractFirebaseController:saveFacebookPages", "saved final batch");
                fullfil();
              })
              .catch((error) => {
                logger.log(
                  "error:AbstractFirebaseController:saveFacebookPages",
                  "unable to save final batch",
                  { error }
                );
                reject(error);
              });
          })
          .catch((error) => {
            logger.log(
              "error:AbstractFirebaseController:saveFacebookPages",
              "unable to save first batch",
              { error }
            );
            reject(error);
          });
      });
    }
    getAllFacebookPages() {
      return new Promise((fullfil, reject) => {
        this.collection("social_accounts")
          .get()
          .then((pages) => {
            const newPages = pages.docs.map((doc) => ({
              ...doc.data()
            }));
            fullfil(newPages);
          })
          .catch(reject);
      });
    }
    getFacebookPage(pageId) {
      return new Promise((fullfil, reject) => {
        this.collection("social_accounts")
          .doc("fb-" + pageId)
          .get()
          .then((page) => {
            fullfil(page.data());
          })
          .catch(reject);
      });
    }
    queryFacebookPages(fieldName, fieldValue) {
      return new Promise((fullfil, reject) => {
        this.collection("social_accounts")
          .where(fieldName, "==", fieldValue)
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }
    updateFacebookPage(pageId, data) {
      return new Promise((fullfil, reject) => {
        this.collection("social_accounts")
          .doc("fb-" + pageId)
          .update(data)
          .then(fullfil)
          .catch(reject);
      });
    }
    loadDailyClicks(campaignId) {
      return new Promise((fullfil, reject) => {
        this.collection("reporting")
          .where("campaignId", "==", campaignId)
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }

    addDailyClicks(campaignId, channel, date, data) {
      return new Promise((fullfil, reject) => {
        this.collection("reporting")
          .where("campaignId", "==", campaignId)
          .where("channel", "==", channel)
          .where("date", "==", date)
          .get()
          .then((querySnapshot) => {
            const existingRecords = [];
            querySnapshot.forEach((doc) => {
              const elem = doc.data();
              elem.id = doc.id;
              existingRecords.push(elem);
            });

            if (existingRecords.length == 0) {
              this.collection("reporting").add(data).then(fullfil).catch(reject);
            } else {
              var firstItem = existingRecords[0];

              if (!firstItem || (firstItem && !firstItem.id)) {
                logger.log("error:AbstractFirebaseController:addDailyClicks", "called", {
                  campaignId,
                  firstItem
                });
                return reject(`No record id found ${campaignId}`);
              }

              this.collection("reporting")
                .doc(firstItem.id)
                .update(data)
                .then(fullfil)
                .catch(reject);
            }
          })
          .catch(reject);
      });
    }
    //==============================================================================
    //
    //==============================================================================
    getSingleEntry(collection, id) {
      return new Promise((fullfil, reject) => {
        this.collection(collection).doc(id).get().then(fullfil).catch(reject);
      });
    }
    updateSingleEntry(collection, id, data) {
      return new Promise((fullfil, reject) => {
        this.collection(collection)
          .doc(id)
          // Be careful, when changing this line, talk to Nils
          .set(data, { merge: true })
          .then(fullfil)
          .catch(reject);
      });
    }
    getCampaignMirror(limit, channels, order = "desc") {
      return new Promise(function (fullfil, reject) {
        firebase
          .firestore()
          .collection("campaigns-mirror")
          .orderBy("createdDate", order == "desc" ? "desc" : undefined)
          .where("channel", "in", channels)
          .limit(limit)
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }
    lastNCampaignsQuery(limit) {
      return new Promise((fullfil, reject) => {
        this.collection("campaigns")
          .where("status", "in", [
            "ACTIVE",
            "PAUSED",
            "SCHEDULED",
            "WAITING_FOR_APPROVAL",
            "IN_PROGRESS",
            "ACTION_REQUIRED",
            "FINISHED"
          ])
          .limit(limit)
          .orderBy("createdDate", "desc")
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }

    getAllActiveCampaigns() {
      return new Promise((fullfil, reject) => {
        this.collection("campaigns")
          .where("status", "in", ["ACTIVE"])
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }

    getCampaignMirrorStartAt(startAt, limit, channels, order = "desc") {
      return new Promise(function (fullfil, reject) {
        firebase
          .firestore()
          .collection("campaigns-mirror")
          .where("channel", "in", channels)
          .limit(limit)
          .orderBy("createdDate", order == "desc" ? "desc" : undefined)
          .startAt(startAt)
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }
    getCampaignMirrorEndAt(endAt, limit, channels, order = "desc") {
      return new Promise(function (fullfil, reject) {
        firebase
          .firestore()
          .collection("campaigns-mirror")
          .where("channel", "in", channels)
          .limit(limit)
          .orderBy("createdDate", order == "desc" ? "desc" : undefined)
          .endAt(endAt)
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }
    getSocialCommentsCount(campaignId) {
      return new Promise(function (fullfil, reject) {
        firebase
          .firestore()
          .collection("social_comment_entries")
          .where("campaignId", "==", campaignId)
          .get()
          .then((querySnapshot) => fullfil(querySnapshot.size))
          .catch(reject);
      });
    }
    getSocialComments(campaignId) {
      return new Promise(function (fullfil, reject) {
        firebase
          .firestore()
          .collection("social_comment_entries")
          .where("campaignId", "==", campaignId)
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }
    // Others
    updateAtsWithCampaignId(campaignId, updateObject) {
      return new Promise((fullfil, reject) => {
        this.collection("ats")
          .doc(campaignId)
          .set(updateObject, { merge: true })
          .then(fullfil)
          .catch(reject);
      });
    }
    saveAts(atsData) {
      return new Promise((fullfil, reject) => {
        this.collection("ats").add(atsData).then(fullfil).catch(reject);
      });
    }
    getAtsOrga(orgaId) {
      return new Promise(function (fullfil, reject) {
        firebase
          .firestore()
          .collection("ats_orga")
          .doc(orgaId)
          .get()
          .then((doc) => fullfil({ id: doc.id, ...doc.data() }))
          .catch(reject);
      });
    }
    getAllAtsForOrga(orgaId) {
      return new Promise(function (fullfil, reject) {
        firebase
          .firestore()
          .collection("ats")
          .where("orgaId", "==", orgaId)
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }

    getAts(campaignId) {
      return new Promise(function (fullfil, reject) {
        firebase
          .firestore()
          .collection("ats")
          .doc(campaignId)
          .get()
          .then((doc) => fullfil({ id: doc.id, ...doc.data() }))
          .catch(reject);
      });
    }
    getAtsTemplatesByType(type) {
      return new Promise((fullfil, reject) => {
        firebase
          .firestore()
          .collection("ats_templates")
          .where("type", "==", type)
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }

    getMessageTemplateByTypeAndOrga(type, orgaId) {
      return new Promise((fullfil, reject) => {
        firebase
          .firestore()
          .collection("message_templates")
          .where("type", "==", type)
          .where("orgaId", "==", orgaId)
          .get()
          .then((querySnapshot) => fullfil(enrichSnapshotWithId(querySnapshot)))
          .catch(reject);
      });
    }

    moveCampaignATSSettings(campaignId, newCampaignId) {
      return new Promise((fullfil, reject) => {
        this.collection("ats")
          .doc(campaignId)
          .get()
          .then((campaign) => {
            const newData = _.extend({}, campaign.data());

            if (!newData) {
              logger.log("error:moveCampaignATSSettings", "No ATS found (1)", {
                campaignId,
                newCampaignId
              });
              return reject("No ATS found");
            }

            if (!newCampaignId) {
              logger.log("error:moveCampaignATSSettings", "No new campaign id passed in (3)", {
                campaignId,
                newCampaignId
              });
              return reject("No ATS found");
            }

            this.collection("ats")
              .doc(newCampaignId)
              .set(newData, { merge: true })
              .then(() => {
                this.collection("ats").doc(campaignId).delete().then(fullfil).catch(reject);
              })
              .then(fullfil)
              .catch(reject);
          })
          .catch((err) => {
            logger.log("error:moveCampaignATSSettings", "No ATS found (2)", {
              err,
              campaignId,
              newCampaignId
            });
            return reject("No ATS found");
          });
      });
    }
    deleteAtsSettings(campaignId) {
      return new Promise((fullfil, reject) => {
        this.collection("ats").doc(campaignId).delete().then(fullfil).catch(reject);
      });
    }

    getCandidate(candidateId) {
      return new Promise(function (fullfil, reject) {
        firebase
          .firestore()
          .collection("candidates")
          .doc(candidateId)
          .get()
          .then((doc) => {
            if (!doc.data()) return reject("Document data is undefined");
            return fullfil({ id: doc.id, ...doc.data() });
          })
          .catch((error) => {
            return reject(error);
          });
      });
    }

    async getMessageById(messageId) {
      return await firebase
        .firestore()
        .collection("messages")
        .doc(messageId)
        .get()
        .then((doc) => ({ id: doc.id, ...doc.data() }));
    }

    async getMessages(campaignId, candidateId) {
      return await firebase
        .firestore()
        .collection("messages")
        .where("campaignId", "==", campaignId)
        .where("candidateId", "==", candidateId)
        .get()
        .then((querySnapshot) => enrichSnapshotWithId(querySnapshot));
    }

    getMessagesByCampaignId(campaignId) {
      return new Promise(function (fullfil, reject) {
        firebase
          .firestore()
          .collection("messages")
          .where("campaignId", "==", campaignId)
          .get()
          .then((qs) => fullfil(enrichSnapshotWithId(qs)))
          .catch(reject);
      });
    }
    getMessagesByCampaignIdAndCreatedAfterDate(campaignId, createdAfter) {
      return new Promise(function (fullfil, reject) {
        firebase
          .firestore()
          .collection("messages")
          .where("campaignId", "==", campaignId)
          .where("createdDate", ">=", createdAfter)
          // TODO: Get only received messages
          .get()
          .then((qs) => fullfil(enrichSnapshotWithId(qs)))
          .catch(reject);
      });
    }
    getCandidatesByCampaignId(campaignId) {
      return new Promise(function (fullfil, reject) {
        firebase
          .firestore()
          .collection("candidates")
          .where("campaignId", "==", campaignId)
          .get()
          .then((qs) => fullfil(enrichSnapshotWithId(qs)))
          .catch(reject);
      });
    }

    async getCandidateRatings(candidateId) {
      return firebase
        .firestore()
        .collection("candidates")
        .doc(candidateId)
        .collection("ratings")
        .get()
        .then((qs) => qs.docs.map((doc) => ({ ...doc.data(), id: doc.id })));
    }
    getCandidateAttachments(candidateId) {
      return new Promise(function (fullfil, reject) {
        firebase
          .firestore()
          .collection("messages")
          .where("candidateId", "==", candidateId)
          .get()
          .then((qs) => {
            let messageData = qs.docs.map((doc) => {
              return {
                ...doc.data(),
                id: doc.id
              };
            });
            messageData = _.filter(messageData, (message) => {
              return message.attachments && message.attachments.length;
            });
            messageData = _.map(messageData, (message) => {
              return message.attachments;
            });
            fullfil(_.flatten(messageData));
          })
          .catch(reject);
      });
    }

    updateCandidate(candidateId, candidateUpdate) {
      return new Promise(function (fullfil, reject) {
        firebase
          .firestore()
          .collection("candidates")
          .doc(candidateId)
          .update(candidateUpdate)
          .then(fullfil)
          .catch(reject);
      });
    }
    updateMessage(messageId, messageUpdate) {
      return new Promise(function (fullfil, reject) {
        firebase
          .firestore()
          .collection("messages")
          .doc(messageId)
          .update(messageUpdate)
          .then(fullfil)
          .catch(reject);
      });
    }
    //==============================================================================
    // Campaign Managers Collection
    //==============================================================================
    getAllCampaignManagers() {
      return new Promise(function (fullfil, reject) {
        firebase
          .firestore()
          .collection("campaign_managers")
          .where("isArchived", "==", false)
          .get()
          .then((qs) => fullfil(enrichSnapshotWithId(qs)))
          .catch(reject);
      });
    }
    getActiveCampaignManagers() {
      return new Promise(function (fullfil, reject) {
        firebase
          .firestore()
          .collection("campaign_managers")
          .where("isActive", "==", true)
          .get()
          .then((qs) => fullfil(enrichSnapshotWithId(qs)))
          .catch(reject);
      });
    }
    getCampaignManagerById(campaignManagerId) {
      return new Promise(function (fullfil, reject) {
        firebase
          .firestore()
          .collection("campaign_managers")
          .doc(campaignManagerId)
          .get()
          .then((doc) =>
            fullfil({
              ...doc.data(),
              id: doc.id
            })
          )
          .catch(reject);
      });
    }
    getCampaignManagerByFrontId(fronUserId) {
      return new Promise(function (fullfil, reject) {
        firebase
          .firestore()
          .collection("campaign_managers")
          .where("frontUserId", "==", fronUserId)
          .get()
          .then((qs) => {
            const doc = qs.docs[0];
            if (!doc) {
              return fullfil(undefined);
            }
            return fullfil({
              ...doc.data(),
              id: doc.id
            });
          })
          .catch(reject);
      });
    }
    updateCampaignManager(campaignManagerId, updateObject) {
      return new Promise(function (fullfil, reject) {
        firebase
          .firestore()
          .collection("campaign_managers")
          .doc(campaignManagerId)
          .update(updateObject)
          .then(fullfil)
          .catch(reject);
      });
    }

    //==============================================================================
    // IDs Collection
    //==============================================================================
    getGuid() {
      return guid();
    }
  }

  // What does this structure mean?
  // Assigns all the methods exported from the Mixins to the FirebaseController class
  const newObj = Object.assign(
    FirebaseController.prototype,
    MixinCampaigns,
    MixinAdsets,
    MixinAdPreview,
    MixinChannelCampaigns,
    MixinCoupons,
    MixinCampaignMembers,
    MixinBookingSessions,
    MixinMediaAssets,
    MixinPrepaidTransactions,
    MixinApplicantSystem,
    MixinReports,
    MixinKeywords,
    MixinAssets,
    MixinUsers,
    MixinCandidates,
    MixinUserLogsFlat,
    MixinOrganization,
    MixinRoles,
    MixinDailyMetricsMixin,
    MixinBrandKitsMixin,
    MixinPrepaidStateMixin,
    MixinCandidateLogs,
    MixinAts,
    // Shared Mixin
    MixinAtsStats,
    MixinAccountSystem,
    MixinMemberSystem,
    MixinOrganizations
  );

  return newObj;
};
